import React, { useEffect, useState } from "react";
import { Trophy, Flame, Lightning, Crown, Medal, Brain, Fire, Barbell } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const ICONS = { Trophy, Flame, Lightning, Crown, Medal, Brain, Fire, Barbell };

export default function Achievements() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/achievements").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="label-tiny text-text-secondary">Loading...</div>
      </div>
    );
  }

  const { streak, stats, achievements } = data;
  const unlocked = achievements.filter((a) => a.unlocked);
  const locked = achievements.filter((a) => !a.unlocked);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="achievements-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// HALL OF VALOR</div>
        <h1 className="font-heading text-4xl sm:text-5xl">ACHIEVEMENTS</h1>
      </div>

      {/* Streak Hero */}
      <div className="tactical-card p-8 mb-8 stripe-bg relative overflow-hidden" data-testid="streak-card">
        <div className="relative z-10 grid md:grid-cols-2 gap-6">
          <div>
            <div className="label-tiny mb-2 flex items-center gap-2">
              <Fire size={14} weight="fill" className="text-brand-accent" /> CURRENT STREAK
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-7xl tabular-nums text-brand-accent neon-yellow">{streak}</span>
              <span className="text-text-secondary font-heading text-xl">DAYS</span>
            </div>
            <p className="text-sm text-text-secondary mt-2">
              {streak === 0 ? "Log a workout today to start the streak." : streak < 3 ? "Solid start. Keep showing up." : streak < 7 ? "Momentum is real." : streak < 30 ? "You're built different." : "Legendary. Unstoppable."}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10">
            <div className="bg-surface-DEFAULT p-4 text-center" data-testid="stat-workouts-total">
              <div className="font-heading text-2xl tabular-nums">{stats.workouts}</div>
              <div className="label-tiny mt-1">WORKOUTS</div>
            </div>
            <div className="bg-surface-DEFAULT p-4 text-center" data-testid="stat-prs-total">
              <div className="font-heading text-2xl tabular-nums">{stats.prs}</div>
              <div className="label-tiny mt-1">PRS</div>
            </div>
            <div className="bg-surface-DEFAULT p-4 text-center" data-testid="stat-programs-total">
              <div className="font-heading text-2xl tabular-nums">{stats.programs}</div>
              <div className="label-tiny mt-1">PROGRAMS</div>
            </div>
          </div>
        </div>
      </div>

      {/* Unlocked */}
      {unlocked.length > 0 && (
        <div className="mb-8">
          <div className="label-tiny mb-4">// UNLOCKED ({unlocked.length})</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="achievements-unlocked">
            {unlocked.map((a) => {
              const Icon = ICONS[a.icon] || Trophy;
              return (
                <div key={a.id} className="tactical-card active p-5 text-center" data-testid={`achievement-${a.id}`}>
                  <Icon size={32} weight="fill" className="text-brand-accent mx-auto mb-3" />
                  <h3 className="font-heading text-base mb-1">{a.name.toUpperCase()}</h3>
                  <p className="text-xs text-text-secondary">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked */}
      {locked.length > 0 && (
        <div>
          <div className="label-tiny mb-4">// IN PROGRESS ({locked.length})</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="achievements-locked">
            {locked.map((a) => {
              const Icon = ICONS[a.icon] || Trophy;
              return (
                <div key={a.id} className="tactical-card p-5 text-center opacity-60 hover:opacity-100 transition-opacity" data-testid={`achievement-${a.id}`}>
                  <Icon size={32} weight="duotone" className="text-text-secondary mx-auto mb-3" />
                  <h3 className="font-heading text-base mb-1">{a.name.toUpperCase()}</h3>
                  <p className="text-xs text-text-secondary mb-3">{a.desc}</p>
                  <Progress value={a.progress_pct} className="h-1 bg-surface-elevated" />
                  <div className="label-tiny mt-2 tabular-nums">{a.value} / {a.threshold}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
